import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  Snackbar,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  Fade,
  Zoom,
  Divider,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Radio,
  RadioGroup,
  FormLabel,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import frLocale from 'date-fns/locale/fr';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import PaymentIcon from '@mui/icons-material/Payment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import ErrorIcon from '@mui/icons-material/Error';
import PhoneIcon from '@mui/icons-material/Phone';
import { blue, green, orange, purple, pink } from '@mui/material/colors';
import axios from 'axios';
import html2pdf from 'html2pdf.js';
import SecretarySidebar from '../components/SecretarySidebar';

// Étapes du formulaire : uniquement "Informations personnelles" pour le présentiel
const steps = ['Informations personnelles'];

interface Document {
  id: string;
  name: string;
  file: File | null;
  status: 'pending' | 'uploaded' | 'verified' | 'rejected';
}

// Modifie RegistrationForm : retire matricule, ajoute cantine et eatsAtCantine
interface RegistrationForm {
  // Informations personnelles
  // matricule: string; // supprimé
  firstName: string;
  lastName: string;
  dateOfBirth: Date | null;
  gender: string;
  address: string;
  city: string;
  // phone: string; // Champ téléphone retiré
  // email: string; // supprimé
  cantine: boolean; // ajouté
  eatsAtCantine?: boolean; // ajouté
  allergy?: string; // ajouté
  studentStatus: 'ancien' | 'nouveau' | ''; // modifié pour avoir deux options mutuellement exclusives

  // Informations parent
  parentFirstName: string;
  parentLastName: string;
  parentPhone: string;
  parentEmail: string;
  parentContact: string;
  emergencyContact: string; // Ajouté

  // Informations académiques (conservées pour payload, mais plus d'étape dédiée)
  previousSchool: string;
  previousClass: string;
  desiredClass: string | number;
  desiredClassName: string;

  // Champ de paiement retiré
  // paymentAmount: string;
}

// Ajoute ce type pour éviter l'erreur TypeScript si besoin
declare global {
  interface Window {
    MonnaieFusion?: any;
  }
}

const Receipt = ({ data, onClose, receiptRef, handleDownload }: {
  data: any,
  onClose: () => void,
  receiptRef: React.RefObject<HTMLDivElement>,
  handleDownload: () => void,
}) => {
  const remaining = (data.total_due || 0) - (data.payment_amount || 0);

  const handlePrint = () => {
    if (receiptRef.current) {
      const printContents = receiptRef.current.innerHTML;
      const printWindow = window.open('', '', 'height=700,width=900');
      if (printWindow) {
        printWindow.document.write('<html><head><title>Reçu d\'inscription</title>');
        printWindow.document.write('<style>body{font-family:sans-serif;} .receipt-container{padding:30px;}</style>');
        printWindow.document.write('</head><body>');
        printWindow.document.write(printContents);
        printWindow.document.write('</body></html>');
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 500);
      }
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minHeight: '100vh',
        justifyContent: 'center',
        background: '#f5f7fa',
        py: 4,
      }}
    >
      <Paper
        ref={receiptRef}
        sx={{
          p: { xs: 2, sm: 5 },
          borderRadius: 4,
          boxShadow: 6,
          maxWidth: 700,
          width: '100%',
          mx: 'auto',
          background: 'white',
          mb: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 0 }}>
          <img src="/img/pages/logologo.webp" alt="Logo" style={{ width: 200, height: 200, objectFit: 'contain' }} />
        </Box>
        
        <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 700, color: 'primary.main', mb: 3 }}>
          Reçu d'inscription
        </Typography>
        <Divider sx={{ mb: 3, width: '100%' }} />

        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, alignSelf: 'flex-start', width: '100%' }}>Informations de l'élève</Typography>
        <Grid container spacing={1} sx={{ width: '100%', mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <Typography><b>Nom & Prénoms:</b> {data.last_name} {data.first_name}</Typography>
            <Typography><b>Matricule:</b> {data.registration_number || 'N/A'}</Typography>
            <Typography><b>Classe:</b> {data.desiredClassName}</Typography>
            <Typography><b>Statut:</b> {data.studentStatus === 'ancien' ? 
              <Chip label="Ancien élève" color="primary" size="small" sx={{ ml: 1 }} /> : 
              <Chip label="Nouveau élève" color="secondary" size="small" sx={{ ml: 1 }} />
            }</Typography>
            <Typography><b>Cantine :</b> {data.cantine ? <Chip label="Oui" color="success" size="small" sx={{ ml: 1 }} /> : <Chip label="Non" color="default" size="small" sx={{ ml: 1 }} />}</Typography>
            {/* Ajout affichage mange à la cantine */}
            {data.cantine ? (
              <>
                <Typography sx={{ mt: 1 }}>
                  <b>L'enfant mange à la cantine :</b> {data.eats_at_cantine === true || data.eats_at_cantine === 1 || data.eats_at_cantine === '1' ? <Chip label="Oui" color="success" size="small" /> : <Chip label="Non" color="default" size="small" />}
                </Typography>
                {/* Allergie éventuelle */}
                {data.allergy && (
                  <Typography sx={{ mt: 1 }}>
                    <b>Allergie(s) éventuelle(s) :</b> <span style={{ color: '#d32f2f', fontWeight: 500 }}>{data.allergy}</span>
                  </Typography>
                )}
              </>
            ) : null}
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography><b>Date d'inscription:</b> {data.date}</Typography>
            <Typography><b>Parent:</b> {data.parent_first_name} {data.parent_last_name}</Typography>
             <Typography><b>Code Parent:</b> <Chip label={data.parent_code} color="secondary" size="small" /></Typography>
            <Typography><b>Contact Parent:</b> {data.parent_phone}</Typography>
          </Grid>
        </Grid>
        
        <Divider sx={{ width: '100%' }}><Chip label="Détails du Paiement" /></Divider>
        
        <Box sx={{width: '100%', mt: 2}}>
          <Table size="small">
            <TableBody>
              <TableRow sx={{ 
                backgroundColor: data.studentStatus === 'ancien' ? '#e3f2fd' : '#f5f5f5',
                '& td': { fontWeight: 600, fontSize: '1.1em' }
              }}>
                <TableCell sx={{ color: data.studentStatus === 'ancien' ? '#1976d2' : '#333' }}>
                  Montant de la scolarité
                  {data.studentStatus === 'ancien' && (
                    <Chip label="Ancien élève" color="primary" size="small" sx={{ ml: 1 }} />
                  )}
                  {data.studentStatus === 'nouveau' && (
                    <Chip label="Nouveau élève" color="secondary" size="small" sx={{ ml: 1 }} />
                  )}
                </TableCell>
                <TableCell align="right" sx={{ color: data.studentStatus === 'ancien' ? '#1976d2' : '#333' }}>
                  {(() => {
                    console.log('=== [DEBUG RECEIPT] ===');
                    console.log('data.total_due:', data.total_due);
                    console.log('data.class_amount:', data.class_amount);
                    console.log('data.studentStatus:', data.studentStatus);
                    console.log('data.is_old_student:', data.is_old_student);
                    console.log('=== [FIN DEBUG RECEIPT] ===');
                    const parsedAmount = Number(data.total_due);
                    const amount = isNaN(parsedAmount) ? 0 : parsedAmount;
                    const isOldStudent = data.studentStatus === 'ancien' || data.is_old_student === 1 || data.is_old_student === true;
                    
                    return (
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {amount.toLocaleString('fr-FR')} F CFA
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          {isOldStudent ? 'Tarif ancien élève' : 'Tarif nouveau élève'}
                        </Typography>
                      </Box>
                    );
                  })()}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                  Le secrétariat
              </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 3, width: '100%' }} />
      </Paper>
      <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', width: '100%', maxWidth: 700, mt: 1 }}>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleDownload}
          sx={{ fontWeight: 600, px: 4, py: 1.5, fontSize: 16 }}
        >
          Télécharger le reçu
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handlePrint}
          sx={{ fontWeight: 600, px: 4, py: 1.5, fontSize: 16 }}
        >
          Imprimer le reçu
        </Button>
      </Box>
      <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', width: '100%', maxWidth: 700, mt: 1 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={onClose}
          sx={{ fontWeight: 600, px: 4, py: 1.5, fontSize: 16 }}
        >
          Fermer
        </Button>
      </Box>
    </Box>
  );
};

// Fonction utilitaire pour convertir un étudiant backend en RegistrationForm
function mapStudentToRegistrationForm(student: any): RegistrationForm {
  student = student || {};
  return {
    // matricule: student.registration_number || '', // supprimé
    firstName: student.first_name || student.firstName || '',
    lastName: student.last_name || student.lastName || '',
    dateOfBirth: student.date_of_birth ? 
      (() => {
        // Si c'est une date ISO avec timezone, on l'ajuste
        if (student.date_of_birth.includes && student.date_of_birth.includes('T')) {
          const date = new Date(student.date_of_birth);
          // Ajouter un jour pour compenser le décalage de timezone
          date.setDate(date.getDate() + 1);
          return date;
        }
        return new Date(student.date_of_birth);
      })() : (student.dateOfBirth || null),
    gender: student.gender || '',
    address: student.address || '',
    city: student.city || '',
    // phone: student.phone || '', // Champ téléphone retiré
    // email: student.email || '', // supprimé
    cantine: typeof student.cantine === 'boolean' ? student.cantine : false, // corrigé
    eatsAtCantine: typeof student.eatsAtCantine === 'boolean' ? student.eatsAtCantine : undefined, // ajouté
    allergy: student.allergy || '', // ajouté
    studentStatus: student.is_old_student === 1 || student.is_old_student === true ? 'ancien' : 'nouveau', // modifié
    previousSchool: student.previous_school || student.previousSchool || '',
    previousClass: student.previous_class || student.previousClass || '',
    desiredClass: '',
    desiredClassName: '',
    parentFirstName: student.parent_first_name || student.parentFirstName || '',
    parentLastName: student.parent_last_name || student.parentLastName || '',
    parentPhone: student.parent_phone || student.parentPhone || '',
    parentEmail: student.parent_email || student.parentEmail || '',
    parentContact: student.parent_contact || student.parentContact || '',
    emergencyContact: student.emergency_contact || '',
            // paymentAmount: '', // Retiré
  };
}

const InscrptionPre = ({ onClose, initialData }: { onClose: () => void, initialData?: any }) => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  // Initialisation du formulaire avec initialData si présente
  const [formData, setFormData] = useState<RegistrationForm>(mapStudentToRegistrationForm(initialData));

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });

  const [receiptData, setReceiptData] = useState<any | null>(null);

  // Correction : ref et download ici
  const receiptRef = useRef<HTMLDivElement>(null);
  const handleDownload = () => {
    if (receiptRef.current) {
      html2pdf().from(receiptRef.current).save('recu-inscription.pdf');
    }
  };

  // Ajoute un état pour la photo de l'élève
  const [childPhoto, setChildPhoto] = useState<File | null>(null);

  const [classes, setClasses] = useState<{ id: number, name: string, amount: number }[]>([]);
  useEffect(() => {
    let isMounted = true;
    
    axios.get('https://lapetiteacademie.ci/api/classes', {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    })
      .then(res => {
        if (isMounted) setClasses(res.data);
      })
      .catch(() => {
        if (isMounted) setClasses([]);
      });
      
    return () => {
      isMounted = false;
    };
  }, []);

  // Supprime la gestion des documents et des étapes multiples
  // Validation simplifiée sans paiement
  const selectedClass = classes.find(c => c.id === Number(formData.desiredClass));
  const eatsAtCantineRequired = formData.cantine;
  const eatsAtCantineValid = !eatsAtCantineRequired || typeof formData.eatsAtCantine === 'boolean';
  const studentStatusValid = formData.studentStatus === 'ancien' || formData.studentStatus === 'nouveau';
  const isFormValid = selectedClass && eatsAtCantineValid && studentStatusValid;

  const handleNext = () => {
    // This function is no longer needed as there's only one step
    // setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    // This function is no longer needed as there's only one step
    // setActiveStep((prevStep) => prevStep - 1);
  };

  const handleFileUpload = (documentId: string, event: React.ChangeEvent<HTMLInputElement>) => {
    // This function is no longer needed as there's no document management
    // const file = event.target.files?.[0];
    // if (file) {
    //   setFormData(prev => ({
    //     ...prev,
    //     documents: prev.documents.map(doc =>
    //       doc.id === documentId
    //         ? { ...doc, file, status: 'uploaded' }
    //         : doc
    //     ),
    //   }));
    // }
  };

  const handleSubmit = async () => {
    try {
      if (!formData.gender) {
        setSnackbar({
          open: true,
          message: 'Veuillez sélectionner le genre',
          severity: 'error',
        });
        return;
      }
      if (!formData.studentStatus || (formData.studentStatus !== 'ancien' && formData.studentStatus !== 'nouveau')) {
        setSnackbar({
          open: true,
          message: 'Veuillez sélectionner le statut de l\'élève (Ancien ou Nouveau)',
          severity: 'error',
        });
        return;
      }
      if (eatsAtCantineRequired && typeof formData.eatsAtCantine !== 'boolean') {
        setSnackbar({
          open: true,
          message: 'Veuillez indiquer si l\'enfant mange à la cantine',
          severity: 'error',
        });
        return;
      }
      // Utilisation de FormData pour multipart/form-data
      const form = new FormData();
      form.append('first_name', formData.firstName);
      form.append('last_name', formData.lastName);
      form.append('date_of_birth', formData.dateOfBirth
        ? formData.dateOfBirth instanceof Date
          ? (() => {
              // Utiliser les méthodes locales pour éviter le décalage de timezone
              const year = formData.dateOfBirth.getFullYear();
              const month = String(formData.dateOfBirth.getMonth() + 1).padStart(2, '0');
              const day = String(formData.dateOfBirth.getDate()).padStart(2, '0');
              return `${year}-${month}-${day}`;
            })()
          : formData.dateOfBirth
        : '');
      form.append('gender', formData.gender);
      form.append('address', formData.address);
      form.append('city', formData.city);
      // form.append('phone', formData.phone); // Champ téléphone retiré
      // form.append('email', formData.email); // supprimé
      form.append('password', formData.lastName); // ou un champ mot de passe saisi (remplace matricule)
      form.append('previous_school', formData.previousSchool);
      form.append('previous_class', formData.previousClass);
      form.append('desired_class', String(formData.desiredClass));
      form.append('special_needs', '');
      form.append('additional_info', '');
      form.append('registration_mode', 'onsite'); // mode présentiel
      form.append('parent_first_name', formData.parentFirstName);
      form.append('parent_last_name', formData.parentLastName);
      form.append('parent_phone', formData.parentPhone);
      form.append('parent_email', formData.parentEmail);
      form.append('parent_contact', formData.parentContact);
      form.append('emergency_contact', formData.emergencyContact);
      // Paiement retiré du formulaire
      // form.append('payment_amount', String(Number(formData.paymentAmount) || 0));
      // Correction : log et conversion stricte du booléen cantine
      console.log('Valeur cantine avant envoi:', formData.cantine, typeof formData.cantine);
      form.append('cantine', formData.cantine === true ? '1' : '0');
      form.append('eats_at_cantine', eatsAtCantineRequired ? (formData.eatsAtCantine ? '1' : '0') : '');
      form.append('allergy', formData.cantine ? (formData.allergy || '') : ''); // ajouté
      form.append('is_old_student', formData.studentStatus === 'ancien' ? '1' : '0'); // modifié
      console.log('=== [DEBUG] Statut envoyé au backend ===');
      console.log('formData.studentStatus:', formData.studentStatus);
      console.log('is_old_student envoyé:', formData.studentStatus === 'ancien' ? '1' : '0');
      console.log('=== [FIN DEBUG] ===');
      if (childPhoto) {
        form.append('child_photo', childPhoto);
      }

      await axios.post(
        'https://lapetiteacademie.ci/api/students',
        form,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      )
        .then(async (response) => {
          const now = new Date();
          console.log('=== [DEBUG] Réponse de création ===');
          console.log('response.data:', response.data);
          console.log('=== [FIN DEBUG] ===');
          // Récupère les infos complètes de l'élève nouvellement créé avec l'année scolaire courante
          const { data: studentData } = await axios.get(
            `https://lapetiteacademie.ci/api/students/${response.data.id}?school_year=${new Date().getFullYear()}-${new Date().getFullYear() + 1}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
              }
            }
          );
          
          // Debug pour voir les données reçues
          console.log('=== [DEBUG] Données de l\'élève récupérées ===');
          console.log('studentData:', studentData);
          console.log('total_due:', studentData.total_due);
          console.log('class_amount:', studentData.class_amount);
          console.log('total_discount:', studentData.total_discount);
          console.log('is_old_student:', studentData.is_old_student);
          console.log('=== [FIN DEBUG] ===');
          setReceiptData({
            ...studentData,
            date: now.toLocaleString('fr-FR', { dateStyle: 'full', timeStyle: 'short' }),
            student_code: response.data.student_code,
            parent_code: response.data.parent_code,
            desiredClassName: classes.find(c => c.id === Number(formData.desiredClass))?.name || '',
            // Paiement retiré
        // payment_amount: formData.paymentAmount,
            cantine: formData.cantine, // Ajouté pour le reçu
            eats_at_cantine: formData.eatsAtCantine, // Ajouté pour le reçu
            allergy: formData.allergy, // Ajouté pour le reçu
            studentStatus: formData.studentStatus, // Ajouté pour le reçu
            // Utiliser le montant calculé par le backend
            total_due: studentData.total_due || 0,
            class_amount: studentData.class_amount || (selectedClass ? selectedClass.amount : 0),
            total_discount: studentData.total_discount || 0,
            reste_a_payer: studentData.reste_a_payer || 0,
            is_old_student: studentData.is_old_student,
          });
          setSnackbar({
            open: true,
            message: 'Inscription soumise avec succès !',
            severity: 'success',
          });
        });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Erreur lors de l\'inscription',
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getDocumentStatusIcon = (status: Document['status']) => {
    // This function is no longer needed as there's no document management
    // switch (status) {
    //   case 'verified':
    //     return <CheckCircleIcon color="success" />;
    //   case 'uploaded':
    //     return <PendingIcon color="primary" />;
    //   case 'rejected':
    //     return <ErrorIcon color="error" />;
    //   default:
    //     return null;
    // }
    return null; // No document status to display
  };

  // Remplace renderStepContent par le contenu unique de "Informations personnelles"
  const renderStepContent = () => {
    return (
      <Grid container spacing={3}>
        {/* Informations de l'élève */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 2, pb: 1, borderBottom: '2px solid', borderColor: 'primary.main' }}>
            Informations de l'élève
          </Typography>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <TextField
            required
            fullWidth
            label="Prénom"
            value={formData.firstName}
            onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            required
            fullWidth
            label="Nom"
            value={formData.lastName}
            onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={frLocale}>
            <DatePicker
              label="Date de naissance"
              value={formData.dateOfBirth}
              onChange={(date) => setFormData({ ...formData, dateOfBirth: date })}
              slotProps={{
                textField: { fullWidth: true },
                popper: { disablePortal: true }
              }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel id="genre-label">Genre</InputLabel>
            <Select
              labelId="genre-label"
              value={formData.gender}
              label="Genre"
              onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
              MenuProps={{ disablePortal: true }}
            >
              <MenuItem value=""><em>Choisir...</em></MenuItem>
              <MenuItem value="M">Masculin</MenuItem>
              <MenuItem value="F">Féminin</MenuItem>
              <MenuItem value="Other">Autre</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            label="Adresse"
            value={formData.address}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            required
            fullWidth
            label="Ville"
            value={formData.city}
            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
            }}
          />
        </Grid>
        {/* Champ téléphone de l'élève retiré */}

        {/* Informations du parent */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mt: 3, mb: 2, pb: 1, borderBottom: '2px solid', borderColor: 'primary.main' }}>
            Informations du parent
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            required
            fullWidth
            label="Prénom du parent"
            value={formData.parentFirstName}
            onChange={(e) => setFormData({ ...formData, parentFirstName: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            required
            fullWidth
            label="Nom du parent"
            value={formData.parentLastName}
            onChange={(e) => setFormData({ ...formData, parentLastName: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            required
            fullWidth
            label="Téléphone du parent"
            value={formData.parentPhone}
            onChange={(e) => {
              const value = e.target.value;
              // N'accepter que les chiffres, +, -, (, ), et espaces
              const filteredValue = value.replace(/[^0-9+\-\(\)\s]/g, '');
              setFormData({ ...formData, parentPhone: filteredValue });
            }}
            inputProps={{ 
              pattern: "[0-9+\\s\\-\\(\\)]*",
              title: "Veuillez saisir un numéro de téléphone valide"
            }}
            helperText="Format: +225 0123456789 ou 0123456789"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            required
            fullWidth
            label="Email du parent"
            value={formData.parentEmail}
            onChange={(e) => setFormData({ ...formData, parentEmail: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            required
            fullWidth
            label="Contact du parent"
            value={formData.parentContact}
            onChange={(e) => {
              const value = e.target.value;
              // N'accepter que les chiffres, +, -, (, ), et espaces
              const filteredValue = value.replace(/[^0-9+\-\(\)\s]/g, '');
              setFormData({ ...formData, parentContact: filteredValue });
            }}
            inputProps={{ 
              pattern: "[0-9+\\s\\-\\(\\)]*",
              title: "Veuillez saisir un numéro de téléphone valide"
            }}
            helperText="Format: +225 0123456789 ou 0123456789"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            required
            fullWidth
            label="Contact à joindre en cas d'urgence"
            value={formData.emergencyContact}
            onChange={(e) => {
              const value = e.target.value;
              // N'accepter que les chiffres, +, -, (, ), et espaces
              const filteredValue = value.replace(/[^0-9+\-\(\)\s]/g, '');
              setFormData({ ...formData, emergencyContact: filteredValue });
            }}
            inputProps={{ 
              pattern: "[0-9+\\s\\-\\(\\)]*",
              title: "Veuillez saisir un numéro de téléphone valide"
            }}
            helperText="Format: +225 0123456789 ou 0123456789"
            InputProps={{
              startAdornment: (
                <PhoneIcon sx={{ color: '#1976d2', mr: 1 }} />
              ),
            }}
          />
        </Grid>

        {/* Informations académiques */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mt: 3, mb: 2, pb: 1, borderBottom: '2px solid', borderColor: 'primary.main' }}>
            Informations académiques
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel id="desired-class-label">Classe souhaitée</InputLabel>
            <Select
              labelId="desired-class-label"
              value={formData.desiredClass}
              label="Classe souhaitée"
              onChange={e => {
                const classId = e.target.value;
                setFormData({ ...formData, desiredClass: classId, desiredClassName: classes.find(c => c.id === Number(classId))?.name || '' });
              }}
            >
              {classes.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Montant de la scolarité retiré */}
        </Grid>
        {/* Champ de paiement retiré */}

        {/* Photo de l'élève */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mt: 3, mb: 2, pb: 1, borderBottom: '2px solid', borderColor: 'primary.main' }}>
            Photo de l'élève
          </Typography>
        </Grid>
        <Grid item xs={12}>
          {childPhoto ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">{childPhoto.name}</Typography>
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => setChildPhoto(null)}
              >
                Retirer
              </Button>
            </Box>
          ) : (
            <Button
              component="label"
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              sx={{ mt: 1 }}
            >
              Sélectionner une photo
              <input
                type="file"
                hidden
                accept="image/jpeg,image/png,image/jpg"
                onChange={e => {
                  const file = e.target.files?.[0];
                  if (file) setChildPhoto(file);
                }}
              />
            </Button>
          )}
        </Grid>

        {/* Statut Ancien/Nouveau Élève */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mt: 3, mb: 2, pb: 1, borderBottom: '2px solid', borderColor: 'primary.main' }}>
            Statut Élève
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <FormControl component="fieldset" required>
            <FormLabel component="legend" sx={{ mb: 2, fontWeight: 500, color: 'text.primary' }}>
              Veuillez sélectionner le statut de l'élève :
            </FormLabel>
            <RadioGroup
              value={formData.studentStatus}
              onChange={(e) => setFormData({ ...formData, studentStatus: e.target.value as 'ancien' | 'nouveau' })}
              sx={{ flexDirection: 'row', gap: 3 }}
            >
              <FormControlLabel
                value="ancien"
                control={<Radio color="primary" />}
                label="Ancien élève (tarif réduit)"
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontWeight: 500,
                    color: formData.studentStatus === 'ancien' ? 'primary.main' : 'text.primary'
                  }
                }}
              />
              <FormControlLabel
                value="nouveau"
                control={<Radio color="primary" />}
                label="Nouveau élève (tarif standard)"
                sx={{
                  '& .MuiFormControlLabel-label': {
                    fontWeight: 500,
                    color: formData.studentStatus === 'nouveau' ? 'primary.main' : 'text.primary'
                  }
                }}
              />
            </RadioGroup>
          </FormControl>
          {formData.studentStatus === 'ancien' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                L'élève sera facturé au tarif des anciens élèves (montant réduit)
              </Typography>
            </Alert>
          )}
          {formData.studentStatus === 'nouveau' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                L'élève sera facturé au tarif des nouveaux élèves (montant standard)
              </Typography>
            </Alert>
          )}
        </Grid>

        {/* Cantine */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mt: 3, mb: 2, pb: 1, borderBottom: '2px solid', borderColor: 'primary.main' }}>
            Cantine
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.cantine}
                onChange={e => setFormData({ ...formData, cantine: e.target.checked, eatsAtCantine: undefined, allergy: '' })}
                color="primary"
              />
            }
            label="Inscrire à la cantine"
          />
        </Grid>
        {formData.cantine && (
          <>
            <Grid item xs={12} md={6}>
              <FormControl component="fieldset" required>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>L'enfant mange à la cantine ?</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControlLabel
                    control={<Checkbox checked={formData.eatsAtCantine === true} onChange={() => setFormData({ ...formData, eatsAtCantine: true })} />}
                    label="Oui"
                  />
                  <FormControlLabel
                    control={<Checkbox checked={formData.eatsAtCantine === false} onChange={() => setFormData({ ...formData, eatsAtCantine: false })} />}
                    label="Non"
                  />
                </Box>
                {!eatsAtCantineValid && <Typography color="error" variant="caption">Veuillez indiquer si l'enfant mange à la cantine</Typography>}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Allergie(s) éventuelle(s)"
                value={formData.allergy || ''}
                onChange={e => setFormData({ ...formData, allergy: e.target.value })}
                placeholder="Ex : arachide, lait, oeuf..."
                helperText="Laisser vide si aucune allergie connue."
              />
            </Grid>
          </>
        )}
      </Grid>
    );
  };

  if (receiptData) {
    return (
      <Box sx={{ display: 'flex' }}>
        <SecretarySidebar />
        <Box component="main" sx={{ flexGrow: 1, width: '100%', p: { xs: 1, md: 4 }, bgcolor: '#f6f8fa', minHeight: '100vh' }}>
          {<Receipt data={receiptData} onClose={onClose} receiptRef={receiptRef} handleDownload={handleDownload} />}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <SecretarySidebar />
      <Box component="main" sx={{ flexGrow: 1, width: '100%', p: { xs: 1, md: 3 }, bgcolor: '#f6f8fa', minHeight: '100vh' }}>
        <Box
          sx={{
            width: '100%',
            maxWidth: '100%', // Utilise toute la largeur disponible
            mx: 'auto',
            p: { xs: 2, sm: 4 },
            borderRadius: 3,
            boxShadow: 3,
            background: 'white',
            position: 'relative',
            transition: 'box-shadow 0.3s',
            animation: 'fadeInUp 0.5s',
            '@keyframes fadeInUp': {
              from: { opacity: 0, transform: 'translateY(40px)' },
              to: { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          <Fade in={true} timeout={500}>
            <Paper sx={{
              p: { xs: 2, sm: 4, md: 6 },
              borderRadius: 3,
              boxShadow: 2,
              background: 'white',
              width: '100%',
              mx: 'auto',
              mb: 2,
            }}>
              <Typography 
                variant="h4" 
                gutterBottom 
                align="center"
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 4,
                }}
              >
                Formulaire d'inscription
              </Typography>

              {/* Plus de Stepper ici */}

              <Zoom in={true} timeout={500}>
                <Box>
                  {renderStepContent()}
                </Box>
              </Zoom>

              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                mt: 4,
                pt: 3,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={onClose}
                  sx={{
                    borderColor: theme.palette.error.main,
                    color: theme.palette.error.main,
                    '&:hover': {
                      borderColor: theme.palette.error.dark,
                      backgroundColor: 'rgba(211, 47, 47, 0.04)',
                    },
                  }}
                >
                  Annuler
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  onClick={handleSubmit}
                  disabled={!isFormValid}
                  sx={{
                    background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
                    color: 'white',
                    '&:hover': {
                      background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`,
                    },
                    px: 4,
                    ml: 2,
                  }}
                >
                  Soumettre
                </Button>
              </Box>
            </Paper>
          </Fade>

          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert
              onClose={handleCloseSnackbar}
              severity={snackbar.severity}
              sx={{ 
                width: '100%',
                boxShadow: 3,
                '& .MuiAlert-icon': {
                  fontSize: 24,
                },
              }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </Box>
    </Box>
  );
};

export default InscrptionPre; 